import type { CatalogItem, SceneElement } from "@calcplan/domain";

export type FormulaAst =
  | { type: "number"; value: number }
  | { type: "variable"; name: string }
  | { type: "unary"; operator: "+" | "-"; argument: FormulaAst }
  | { type: "binary"; operator: "+" | "-" | "*" | "/" | "%" | "^"; left: FormulaAst; right: FormulaAst }
  | { type: "call"; name: FormulaFunction; arguments: FormulaAst[] };

type FormulaFunction = "min" | "max" | "clamp" | "abs" | "round" | "floor" | "ceil";

export interface CompiledFormula {
  source: string;
  ast: FormulaAst;
  dependencies: string[];
  unit: "mm" | "scalar";
}

interface Token {
  type: "number" | "variable" | "identifier" | "operator" | "left" | "right" | "comma" | "eof";
  value: string;
}

const VARIABLE_ALIASES: Record<string, string> = {
  PARENTWIDTH: "PARENTWIDTH",
  PW: "PARENTWIDTH",
  PARENTHEIGHT: "PARENTHEIGHT",
  PH: "PARENTHEIGHT",
  PARENTDEPTH: "PARENTDEPTH",
  PD: "PARENTDEPTH",
  WIDTH: "WIDTH",
  W: "WIDTH",
  HEIGHT: "HEIGHT",
  H: "HEIGHT",
  DEPTH: "DEPTH",
  D: "DEPTH",
};

const FUNCTIONS = new Set<FormulaFunction>(["min", "max", "clamp", "abs", "round", "floor", "ceil"]);

export class FormulaError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "FormulaError";
  }
}

export function compileFormula(source: string, unit: "mm" | "scalar" = "mm"): CompiledFormula {
  const tokens = tokenize(source);
  let cursor = 0;
  const peek = () => tokens[cursor] ?? { type: "eof", value: "" };
  const consume = () => tokens[cursor++] ?? { type: "eof", value: "" };

  function parseExpression(minBindingPower = 0): FormulaAst {
    const token = consume();
    let left: FormulaAst;
    if (token.type === "number") {
      left = { type: "number", value: Number(token.value) };
    } else if (token.type === "variable") {
      left = { type: "variable", name: normalizeVariable(token.value) };
    } else if (token.type === "operator" && (token.value === "+" || token.value === "-")) {
      left = { type: "unary", operator: token.value, argument: parseExpression(70) };
    } else if (token.type === "left") {
      left = parseExpression();
      if (consume().type !== "right") throw new FormulaError("FORMULA_SYNTAX", "Parêntese não fechado.");
    } else if (token.type === "identifier") {
      const name = token.value.toLocaleLowerCase("en-US") as FormulaFunction;
      if (!FUNCTIONS.has(name)) throw new FormulaError("FORMULA_FUNCTION_UNKNOWN", `Função não permitida: ${token.value}.`);
      if (consume().type !== "left") throw new FormulaError("FORMULA_SYNTAX", "A função exige parênteses.");
      const args: FormulaAst[] = [];
      if (peek().type !== "right") {
        while (true) {
          args.push(parseExpression());
          if (peek().type !== "comma") break;
          consume();
        }
      }
      if (consume().type !== "right") throw new FormulaError("FORMULA_SYNTAX", "Parêntese da função não fechado.");
      validateFunctionArity(name, args.length);
      left = { type: "call", name, arguments: args };
    } else {
      throw new FormulaError("FORMULA_SYNTAX", `Token inesperado: ${token.value || "fim"}.`);
    }

    while (true) {
      const operator = peek();
      if (operator.type !== "operator" || !["+", "-", "*", "/", "%", "^"].includes(operator.value)) break;
      const [leftPower, rightPower] = bindingPower(operator.value);
      if (leftPower < minBindingPower) break;
      consume();
      left = {
        type: "binary",
        operator: operator.value as Extract<FormulaAst, { type: "binary" }>["operator"],
        left,
        right: parseExpression(rightPower),
      };
    }
    return left;
  }

  const ast = parseExpression();
  if (peek().type !== "eof") throw new FormulaError("FORMULA_SYNTAX", `Token inesperado: ${peek().value}.`);
  const dependencies = [...collectDependencies(ast)].sort();
  return { source, ast, dependencies, unit };
}

export function evaluateFormula(
  formula: CompiledFormula | FormulaAst,
  context: Record<string, number>,
): number {
  const ast = "ast" in formula ? formula.ast : formula;
  const evaluate = (node: FormulaAst): number => {
    switch (node.type) {
      case "number":
        return node.value;
      case "variable": {
        const value = context[node.name];
        if (!Number.isFinite(value)) {
          throw new FormulaError("FORMULA_TOKEN_UNKNOWN", `Token desconhecido: ${node.name}.`);
        }
        return value!;
      }
      case "unary":
        return node.operator === "-" ? -evaluate(node.argument) : evaluate(node.argument);
      case "binary": {
        const left = evaluate(node.left);
        const right = evaluate(node.right);
        if ((node.operator === "/" || node.operator === "%") && right === 0) {
          throw new FormulaError("FORMULA_DIVISION_BY_ZERO", "Divisão por zero.");
        }
        const value =
          node.operator === "+" ? left + right
            : node.operator === "-" ? left - right
              : node.operator === "*" ? left * right
                : node.operator === "/" ? left / right
                  : node.operator === "%" ? left % right
                    : Math.pow(left, right);
        if (node.operator === "^" && Math.abs(right) > 12) {
          throw new FormulaError("FORMULA_POWER_LIMIT", "Expoente fora do limite permitido.");
        }
        return finite(value);
      }
      case "call": {
        const values = node.arguments.map(evaluate);
        const value =
          node.name === "min" ? Math.min(...values)
            : node.name === "max" ? Math.max(...values)
              : node.name === "clamp" ? Math.max(values[1]!, Math.min(values[2]!, values[0]!))
                : node.name === "abs" ? Math.abs(values[0]!)
                  : node.name === "round" ? Math.round(values[0]!)
                    : node.name === "floor" ? Math.floor(values[0]!)
                      : Math.ceil(values[0]!);
        return finite(value);
      }
    }
  };
  return finite(evaluate(ast));
}

export function resolveFormulaDag(
  formulas: Record<string, string>,
  initialContext: Record<string, number>,
): Record<string, number> {
  const definitions = Object.entries(formulas).map(([key, source]) => ({
    key,
    normalizedKey: normalizeVariable(key),
    formula: compileFormula(source),
  }));
  const compiled = new Map(definitions.map((definition) => [definition.normalizedKey, definition]));
  const result = Object.fromEntries(
    Object.entries(initialContext).map(([key, value]) => [normalizeVariable(key), value]),
  );
  const visiting = new Set<string>();
  const resolved = new Set<string>(Object.keys(result));

  const resolve = (key: string): number => {
    const normalizedKey = normalizeVariable(key);
    if (resolved.has(normalizedKey)) return result[normalizedKey]!;
    const definition = compiled.get(normalizedKey);
    if (!definition) throw new FormulaError("FORMULA_TOKEN_UNKNOWN", `Token desconhecido: ${key}.`);
    if (visiting.has(normalizedKey)) throw new FormulaError("FORMULA_CYCLE", `Ciclo detectado em ${key}.`);
    visiting.add(normalizedKey);
    for (const dependency of definition.formula.dependencies) {
      if (compiled.has(dependency) && !resolved.has(dependency)) result[dependency] = resolve(dependency);
    }
    result[normalizedKey] = evaluateFormula(definition.formula, result);
    visiting.delete(normalizedKey);
    resolved.add(normalizedKey);
    return result[normalizedKey]!;
  };

  for (const key of compiled.keys()) resolve(key);
  return Object.fromEntries(
    definitions.map((definition) => [definition.key, result[definition.normalizedKey]!]),
  );
}

export function resizeElement(
  source: SceneElement,
  item: CatalogItem,
  dimensions: Partial<SceneElement["dimensionsMm"]>,
  parentDimensions?: SceneElement["dimensionsMm"],
): SceneElement {
  const next = structuredClone(source);
  const target = { ...next.dimensionsMm, ...dimensions };
  const resize = item.resize;
  if (resize) {
    for (const key of ["width", "height", "depth"] as const) {
      const axis = resize[key];
      const value = target[key];
      if (!axis.enabled && value !== next.dimensionsMm[key]) {
        throw new FormulaError("RESIZE_AXIS_DISABLED", `O eixo ${key} não é redimensionável.`);
      }
      // Only what the caller is actually changing is judged. Holding the other
      // axes to the same rules refused the whole resize over a size the user
      // never touched, and left them unable to change the module at all.
      if (dimensions[key] === undefined) continue;
      if ((axis.minMm !== null && value < axis.minMm) || (axis.maxMm !== null && value > axis.maxMm)) {
        throw new FormulaError("RESIZE_OUT_OF_RANGE", `${key} fora do limite.`);
      }
      // The step only means something measured from somewhere. A declared
      // minimum is that somewhere; without one there is no grid to be off, and
      // an imported component is rarely a whole number of steps wide anyway —
      // demanding it here rejected every resize of a 615 mm deep module, on the
      // axes the user had not even touched.
      const fromMinimum = value - (axis.minMm ?? 0);
      if (
        axis.minMm !== null
        && axis.stepMm > 0
        && Math.abs(fromMinimum / axis.stepMm - Math.round(fromMinimum / axis.stepMm)) > 1e-6
      ) {
        throw new FormulaError("RESIZE_STEP_INVALID", `${key} não respeita o passo.`);
      }
    }
  }
  if (resize?.preserveAspect) {
    const changedKeys = (["width", "height", "depth"] as const).filter(
      (key) => dimensions[key] !== undefined && dimensions[key] !== source.dimensionsMm[key],
    );
    if (changedKeys.length === 1) {
      const changed = changedKeys[0]!;
      const ratio = target[changed] / source.dimensionsMm[changed];
      for (const key of ["width", "height", "depth"] as const) {
        if (key !== changed && resize[key].enabled) target[key] = source.dimensionsMm[key] * ratio;
      }
    }
  }
  next.dimensionsMm = target;
  next.revision += 1;
  const context = {
    WIDTH: target.width,
    HEIGHT: target.height,
    DEPTH: target.depth,
    PARENTWIDTH: parentDimensions?.width ?? target.width,
    PARENTHEIGHT: parentDimensions?.height ?? target.height,
    PARENTDEPTH: parentDimensions?.depth ?? target.depth,
    ...Object.fromEntries(
      Object.entries(next.parameters)
        .filter((entry): entry is [string, number] => typeof entry[1] === "number"),
    ),
  };
  if (item.formulas) {
    const resolved = resolveFormulaDag(item.formulas, context);
    next.parameters = { ...next.parameters, ...resolved };
  }
  next.parameters.resizeAudit = {
    itemRevision: item.revision ?? "1.0.0",
    previous: source.dimensionsMm,
    current: target,
  };
  return next;
}

function tokenize(source: string): Token[] {
  const result: Token[] = [];
  let cursor = 0;
  while (cursor < source.length) {
    const char = source[cursor]!;
    if (/\s/.test(char)) {
      cursor += 1;
      continue;
    }
    if (/[0-9.]/.test(char)) {
      const match = source.slice(cursor).match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
      if (!match) throw new FormulaError("FORMULA_SYNTAX", "Número inválido.");
      result.push({ type: "number", value: match[0] });
      cursor += match[0].length;
      continue;
    }
    if (char === "$") {
      const end = source.indexOf("$", cursor + 1);
      if (end < 0) throw new FormulaError("FORMULA_SYNTAX", "Token sem fechamento.");
      result.push({ type: "variable", value: source.slice(cursor + 1, end) });
      cursor = end + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(char)) {
      const match = source.slice(cursor).match(/^[A-Za-z_][A-Za-z0-9_]*/)!;
      result.push({ type: "identifier", value: match[0] });
      cursor += match[0].length;
      continue;
    }
    if ("+-*/%^".includes(char)) result.push({ type: "operator", value: char });
    else if (char === "(") result.push({ type: "left", value: char });
    else if (char === ")") result.push({ type: "right", value: char });
    else if (char === ",") result.push({ type: "comma", value: char });
    else throw new FormulaError("FORMULA_SYNTAX", `Caractere não permitido: ${char}.`);
    cursor += 1;
  }
  result.push({ type: "eof", value: "" });
  return result;
}

function normalizeVariable(value: string): string {
  const upper = value.toLocaleUpperCase("en-US");
  return VARIABLE_ALIASES[upper] ?? upper;
}

function bindingPower(operator: string): [number, number] {
  if (operator === "+" || operator === "-") return [10, 11];
  if (operator === "*" || operator === "/" || operator === "%") return [20, 21];
  return [30, 30];
}

function collectDependencies(ast: FormulaAst, output = new Set<string>()): Set<string> {
  if (ast.type === "variable") output.add(ast.name);
  if (ast.type === "unary") collectDependencies(ast.argument, output);
  if (ast.type === "binary") {
    collectDependencies(ast.left, output);
    collectDependencies(ast.right, output);
  }
  if (ast.type === "call") ast.arguments.forEach((argument) => collectDependencies(argument, output));
  return output;
}

function validateFunctionArity(name: FormulaFunction, count: number) {
  if (name === "clamp" && count !== 3) throw new FormulaError("FORMULA_ARITY", "clamp exige três argumentos.");
  if (["abs", "round", "floor", "ceil"].includes(name) && count !== 1) {
    throw new FormulaError("FORMULA_ARITY", `${name} exige um argumento.`);
  }
  if ((name === "min" || name === "max") && count < 1) {
    throw new FormulaError("FORMULA_ARITY", `${name} exige ao menos um argumento.`);
  }
}

function finite(value: number): number {
  if (!Number.isFinite(value)) throw new FormulaError("FORMULA_NON_FINITE", "A fórmula produziu valor não finito.");
  return value;
}
